import { useEffect, useCallback } from 'react';
import { playAudioAsync, stopAudio, setAudioIndex } from 'src/redux/slices/audio';
import { useAppDispatch, useAppSelector } from 'src/redux/store';

interface UseAudioPlayerProps {
    getAudioUri?: (text: string, lang: string) => Promise<string>;
}

export const useAudioPlayer = ({ getAudioUri }: UseAudioPlayerProps) => {
    const dispatch = useAppDispatch();
    const { audioLoading, audioIndex, isPlaying, isAPILoading } = useAppSelector(state => state.audio);

    const playAudio = useCallback(async (text: string, index: number) => {
        if (audioLoading) return;

        try {
            const userProfile = JSON.parse(localStorage.getItem('userDetails') as string);
            const lang = userProfile?.voice_lang || 'en';

            dispatch(stopAudio()); // Stop any playing audio first
            dispatch(setAudioIndex(index));
            await dispatch(playAudioAsync({
                text,
                lang,
                getAudioUri,
                dispatch
            })).unwrap();
        } catch (error) {
            console.error('Audio playback error:', error);
            dispatch(stopAudio());
        }
    }, [audioLoading, dispatch, getAudioUri]);

    const handleStopAudio = useCallback(() => {
        dispatch(stopAudio());
    }, [dispatch]);

    // Clean up on unmount or page visibility change
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                dispatch(stopAudio());
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            dispatch(stopAudio());
        };
    }, [dispatch]);

    return {
        playAudio,
        stopAudio: handleStopAudio,
        audioLoading,
        audioIndex,
        isPlaying,
        isAPILoading
    };
};